/**
 * Me Page - Account Information
 *
 * Displays current user's account details including:
 * - Username and email
 * - Role and company
 * - User preferences (click behavior, crop mode, language)
 * - Permissions
 * - Camera serials (for photographers)
 */
import { useTranslation } from 'react-i18next';
import { updateMyLanguage } from '../api';
import { useUIStore } from '../stores/useUIStore';
import styles from './Me.module.css';

const roleClasses = {
  admin: styles.roleAdmin,
  editor: styles.roleEditor,
  viewer: styles.roleViewer,
  photographer: styles.rolePhotographer,
};

function SegmentedControl({ options, value, onChange }) {
  return (
    <div className={styles.segmentedControl}>
      {options.map((option) => (
        <button
          key={option.value}
          className={`${styles.segment} ${value === option.value ? styles.segmentActive : ''}`}
          onClick={() => onChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Francais' },
];

export default function Me({ user }) {
  const { t, i18n } = useTranslation();

  // User preferences from store
  const autoEnterCropMode = useUIStore((state) => state.autoEnterCropMode);
  const setAutoEnterCropMode = useUIStore((state) => state.setAutoEnterCropMode);

  if (!user) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('me.loading')}</div>
      </div>
    );
  }

  const handleLanguageChange = async (lang) => {
    i18n.changeLanguage(lang);
    try {
      await updateMyLanguage(lang);
    } catch (e) {
      console.warn('Failed to save language preference:', e);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1>{t('me.title')}</h1>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('me.profile')}</h2>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('me.username')}</span>
              <span className={styles.fieldValue}>{user.username}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('me.email')}</span>
              <span className={styles.fieldValue}>{user.email || '-'}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('me.role')}</span>
              <span className={`${styles.roleBadge} ${roleClasses[user.role] || ''}`}>
                {t(`roles.${user.role}`, { defaultValue: user.role })}
              </span>
            </div>
            {user.company && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('me.company')}</span>
                <span className={styles.fieldValue}>{user.company.name}</span>
              </div>
            )}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('me.preferences')}</h2>
          <div className={styles.preferenceItem}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={autoEnterCropMode}
                onChange={(e) => setAutoEnterCropMode(e.target.checked)}
              />
              {t('me.autoEnterCrop')}
            </label>
            <p className={styles.preferenceHint}>
              {t('me.autoEnterCropHint')}
            </p>
          </div>
          <div className={styles.preferenceItem}>
            <span className={styles.preferenceLabel}>{t('me.language')}</span>
            <SegmentedControl
              options={LANGUAGE_OPTIONS}
              value={i18n.language}
              onChange={handleLanguageChange}
            />
            <p className={styles.preferenceHint}>
              {t('me.languageHint')}
            </p>
          </div>
        </section>

        {user.permissions && Object.keys(user.permissions).length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('me.permissions')}</h2>
            <div className={styles.permissions}>
              {Object.entries(user.permissions).map(([key, value]) => (
                <div
                  key={key}
                  className={`${styles.permissionItem} ${value ? styles.permissionGranted : styles.permissionDenied}`}
                >
                  <span className={styles.permissionIcon}>{value ? '+' : '-'}</span>
                  <span className={styles.permissionName}>{key.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {user.camera_serials?.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('me.cameras')}</h2>
            <div className={styles.cameras}>
              {user.camera_serials.map((serial) => (
                <code key={serial} className={styles.cameraSerial}>
                  {serial}
                </code>
              ))}
            </div>
          </section>
        )}

        <section className={`${styles.section} ${styles.metaSection}`}>
          <h2 className={styles.sectionTitle}>{t('me.meta')}</h2>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('me.userId')}</span>
              <code className={styles.fieldCode}>{user.id}</code>
            </div>
            {user.is_service_account && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('me.accountType')}</span>
                <span className={styles.serviceBadge}>{t('me.serviceAccount')}</span>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
